import React, { useState, useMemo } from 'react';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { usePublicCollection } from '../../hooks/usePublicCollection';
import { SERVICES } from '../../services/publicContent';
import { Layout } from '../layout/Layout';
import { SEOHead } from '../SEOHead';
import { buildBreadcrumbSchema } from '../../utils/seo';
import { useLocalizedField } from '../../hooks/useLocalizedField';

// Firestore docs are not schema-enforced — a service saved without a price
// used to crash the whole page on `svc.price.toLocaleString()`.
const formatPrice = (price) => {
    const value = Number(price);
    return Number.isFinite(value) ? value.toLocaleString() : '—';
};
export const PriceList = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const pick = useLocalizedField();
    const { items: services, loading, error } = usePublicCollection(SERVICES);
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

    // SEO
    const seoTitle = t('seo.pricesTitle');
    const seoDescription = t('seo.pricesDescription');

    // Group services by base (Ukrainian) category to keep stable keys
    // Memoized — only recomputes when services array changes
    const groupedByBase = useMemo(() =>
        services.reduce((acc, svc) => {
            const baseKey = svc.category;
            if (!acc[baseKey]) acc[baseKey] = [];
            acc[baseKey].push(svc);
            return acc;
        }, {}),
        [services]
    );

    const baseCategories = useMemo(() => Object.keys(groupedByBase), [groupedByBase]);

    // Build display label for each base category
    const getCategoryLabel = (baseCat) => {
        if (language === 'en') {
            const svc = groupedByBase[baseCat]?.[0];
            return svc?.categoryEn || baseCat;
        }
        return baseCat;
    };    // Clamp index if categories changed
    const safeIndex = baseCategories.length > 0
        ? Math.min(activeCategoryIndex, baseCategories.length - 1)
        : 0;
    const activeBaseCategory = baseCategories[safeIndex] || null;

    return (
        <Layout>
            <SEOHead
                title={seoTitle}
                description={seoDescription}
                path="/prices"
                schema={buildBreadcrumbSchema([
                    { name: t('nav.home'), path: '/' },
                    { name: t('nav.prices'), path: '/prices' },
                ])}
            />

            {/* Page header */}
            <section className="bg-navy py-12 sm:py-16 md:py-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
                        {t('prices.title')}
                    </h1>
                    <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
                        {t('prices.subtitle')}
                    </p>
                </div>
            </section>

            {/* Price content */}
            <div className="py-10 sm:py-14 md:py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="flex justify-center items-center min-h-[60vh]">
                            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-20">
                            <p className="text-gray-600">{t('errors.prices')}</p>
                        </div>
                    ) : (
                        <>
                            {/* Category tabs */}
                            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
                                {baseCategories.map((baseCat, idx) => (
                                    <button
                                        key={baseCat}
                                        onClick={() => setActiveCategoryIndex(idx)}
                                        type="button"
                                        className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-gold/40 ${
                                            safeIndex === idx
                                                ? 'bg-gold text-white shadow-md'
                                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                    >
                                        {getCategoryLabel(baseCat)}
                                    </button>
                                ))}
                            </div>

                            {/* Price table for active category */}
                            {activeBaseCategory && groupedByBase[activeBaseCategory] && (
                                <div className="max-w-3xl mx-auto">
                                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                                        <div className="bg-navy px-6 py-4 flex items-center justify-between">
                                            <h2 className="text-lg sm:text-xl font-bold text-white">{getCategoryLabel(activeBaseCategory)}</h2>
                                            <span className="text-sm text-gray-300">{t('prices.currency')}</span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {groupedByBase[activeBaseCategory].map((svc, idx) => (
                                                <div
                                                    key={svc.id}
                                                    className={`flex items-center justify-between px-6 py-4 sm:py-5 hover:bg-gray-50 transition-colors ${
                                                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                                    }`}
                                                >
                                                    <span className="text-sm sm:text-base text-gray-700 font-medium pr-4">
                                                        {pick(svc, 'name')}
                                                    </span>
                                                    <span className="text-base sm:text-lg font-bold text-gold whitespace-nowrap">
                                                        {formatPrice(svc.price)} ₴
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* All categories overview */}
                            <div className="mt-12 sm:mt-16 max-w-4xl mx-auto">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 text-center mb-8">
                                    {t('prices.allCategories')}
                                </h3>
                                <div className="space-y-6">
                                    {baseCategories.map((baseCat, idx) => (
                                        <div key={baseCat} className="bg-white rounded-xl shadow-md overflow-hidden">
                                            <button
                                                onClick={() => setActiveCategoryIndex(idx)}
                                                type="button"
                                                className="w-full bg-navy px-6 py-3.5 text-left hover:bg-[#2d4367] transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gold/60"
                                            >
                                                <h4 className="text-base sm:text-lg font-bold text-white">{getCategoryLabel(baseCat)}</h4>
                                            </button>
                                            <div className="divide-y divide-gray-100">
                                                {groupedByBase[baseCat].map(svc => (
                                                    <div key={svc.id} className="flex items-center justify-between px-6 py-3 sm:py-4">
                                                        <span className="text-sm sm:text-base text-gray-700 pr-4">
                                                            {pick(svc, 'name')}
                                                        </span>
                                                        <span className="text-sm sm:text-base font-bold text-gold whitespace-nowrap">
                                                            {formatPrice(svc.price)} ₴
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Disclaimer */}
                            <p className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto">
                                {t('prices.disclaimer')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

